import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, DatePicker, Divider, Modal, Progress, Select, Space, Typography } from 'antd'
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { toastError, toastSuccess, toastWarning } from '../../utils/toast'
import { apiCreateReportTransactionConnectionAll, apiGetReportDownload, apiGetReportStatus } from '../../api'
import { filterOptionByLabel } from '../../utils/filter-utils'

interface CompanyOption {
    label: string
    value: string
}

interface PartnerError {
    cpCode: string
    error: string
}

interface ReportDownloadAllModalProps {
    title: string
    open: boolean
    onClose: () => void
    companyOptions: CompanyOption[]
}

// Recommended max partners in one report — soft warning, not blocking.
const CP_WARN_THRESHOLD = 30

// FE polling timeout for the ALL variant: 30 partners * ~15-20s per partner
// can approach 10 minutes worst-case. Set to 10 minutes with 3-second poll.
const POLL_TIMEOUT_MS = 10 * 60 * 1000

const ReportDownloadAllModal = ({ title, open, onClose, companyOptions }: ReportDownloadAllModalProps) => {
    const [selectedCpCodes, setSelectedCpCodes] = useState<string[]>([])
    const [reportType, setReportType] = useState<string>('DATE')
    const [reportFromDate, setReportFromDate] = useState<string>(dayjs().format('YYYY-MM-DD'))
    const [reportToDate, setReportToDate] = useState<string>(dayjs().format('YYYY-MM-DD'))
    const [reportDate, setReportDate] = useState<string>('')
    const [reportStatus, setReportStatus] = useState<'idle' | 'polling' | 'success' | 'failed'>('idle')
    const [reportError, setReportError] = useState<string>('')
    const [progress, setProgress] = useState<{ total: number; completed: number; failed: number } | null>(null)
    const [partialFailures, setPartialFailures] = useState<PartnerError[]>([])
    const pollingRef = useRef<any>(null)
    const pollingTimeoutRef = useRef<any>(null)
    const activeReportIdRef = useRef<string>('')

    const stopPolling = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current)
            pollingRef.current = null
        }
        if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current)
            pollingTimeoutRef.current = null
        }
    }, [])

    useEffect(() => {
        return () => stopPolling()
    }, [stopPolling])

    const resetForm = () => {
        stopPolling()
        setSelectedCpCodes([])
        setReportType('DATE')
        setReportDate('')
        setReportFromDate(dayjs().format('YYYY-MM-DD'))
        setReportToDate(dayjs().format('YYYY-MM-DD'))
        setReportStatus('idle')
        setReportError('')
        setProgress(null)
        setPartialFailures([])
        activeReportIdRef.current = ''
    }

    const handleClose = () => {
        resetForm()
        onClose()
    }

    const handleSelectAll = () => {
        setSelectedCpCodes(companyOptions.map((o) => o.value))
    }

    const handleClearAll = () => {
        setSelectedCpCodes([])
    }

    const handleCreateReport = async () => {
        if (selectedCpCodes.length === 0) {
            toastError('Vui lòng chọn ít nhất một đối tác!')
            return
        }
        if (!reportType) {
            toastError('Vui lòng chọn loại báo cáo!')
            return
        }
        if (reportType === 'DATE' && (!reportFromDate || !reportToDate)) {
            toastError('Vui lòng chọn ngày bắt đầu và ngày kết thúc!')
            return
        }
        if (reportType !== 'DATE' && !reportDate) {
            toastError('Vui lòng chọn thời gian!')
            return
        }

        try {
            setReportStatus('polling')
            setReportError('')
            setProgress({ total: selectedCpCodes.length, completed: 0, failed: 0 })
            setPartialFailures([])

            const params: any = { cpCodes: selectedCpCodes, type: reportType }
            if (reportType === 'DATE') {
                params.startDate = reportFromDate
                params.endDate = reportToDate
            } else {
                params.date = reportDate
            }

            const resp = await apiCreateReportTransactionConnectionAll(params)
            const reportId = resp.data?.reportId
            if (!reportId) {
                setReportStatus('failed')
                setReportError('Không nhận được mã báo cáo')
                return
            }
            activeReportIdRef.current = reportId
            // Use first selected cpCode for status/download calls (backend AuthZ
            // accepts any cpCode in the report's scope).
            const scopeCpCode = selectedCpCodes[0]

            const retryAfterMs = (resp.data?.retryAfterSeconds || 3) * 1000
            pollingRef.current = setInterval(async () => {
                try {
                    const statusResp = await apiGetReportStatus({ reportId, cpCode: scopeCpCode })
                    const status = statusResp.data?.status
                    if (statusResp.data?.progress) {
                        setProgress(statusResp.data.progress)
                    }

                    if (status === 'SUCCESS') {
                        stopPolling()
                        const failures: PartnerError[] = statusResp.data?.partialFailures || []
                        setPartialFailures(failures)
                        try {
                            const dlResp = await apiGetReportDownload({ reportId, cpCode: scopeCpCode })
                            if (dlResp.data?.downloadUrl) {
                                window.open(dlResp.data.downloadUrl, '_blank')
                                setReportStatus('success')
                                if (failures.length > 0) {
                                    toastWarning(`${failures.length}/${selectedCpCodes.length} đối tác lỗi, xem sheet Summary trong file`)
                                } else {
                                    toastSuccess('Tải báo cáo thành công!')
                                }
                            } else {
                                setReportStatus('failed')
                                setReportError('Không lấy được link tải xuống')
                            }
                        } catch {
                            setReportStatus('failed')
                            setReportError('Lỗi khi lấy link tải xuống')
                        }
                    } else if (status === 'FAILED') {
                        stopPolling()
                        setReportStatus('failed')
                        setReportError(statusResp.data?.error || 'Tạo báo cáo thất bại')
                    }
                } catch {
                    stopPolling()
                    setReportStatus('failed')
                    setReportError('Lỗi khi kiểm tra trạng thái báo cáo')
                }
            }, retryAfterMs)

            pollingTimeoutRef.current = setTimeout(() => {
                stopPolling()
                setReportStatus('failed')
                setReportError('Quá thời gian chờ (10 phút). Vui lòng thử lại với ít đối tác hơn.')
            }, POLL_TIMEOUT_MS)
        } catch {
            setReportStatus('failed')
            setReportError('Có lỗi xảy ra khi tạo báo cáo!')
        }
    }

    const getDatePicker = () => {
        if (reportType === 'MONTH') return 'month'
        if (reportType === 'QUARTER') return 'quarter'
        return 'date'
    }

    const getDateFormat = () => {
        if (reportType === 'MONTH') return 'YYYY-MM'
        if (reportType === 'QUARTER') return 'YYYY-[Q]Q'
        return 'YYYY-MM-DD'
    }

    const progressPercent = progress && progress.total > 0
        ? Math.round((progress.completed / progress.total) * 100)
        : 0

    return (
        <Modal
            title={title}
            open={open}
            onCancel={handleClose}
            width={640}
            footer={reportStatus === 'idle' ? [
                <Button key="create" onClick={handleCreateReport} style={{ background: '#008965', color: 'white' }}>
                    Tạo báo cáo
                </Button>,
                <Button key="close" onClick={handleClose} style={{ background: 'gray', color: 'white' }}>
                    Đóng
                </Button>,
            ] : [
                <Button key="close" onClick={handleClose} style={{ background: 'gray', color: 'white' }}>
                    Đóng
                </Button>,
            ]}
        >
            <Divider />
            {reportStatus === 'idle' && (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <p style={{ margin: 0 }}>Chọn các đối tác</p>
                        <Space>
                            <Button size="small" onClick={handleSelectAll}>Chọn tất cả</Button>
                            <Button size="small" onClick={handleClearAll}>Bỏ chọn</Button>
                        </Space>
                    </div>
                    <Select
                        mode="multiple"
                        style={{ width: '100%', marginTop: 8 }}
                        showSearch
                        placeholder="Chọn các đối tác"
                        options={companyOptions}
                        value={selectedCpCodes}
                        onChange={(values) => setSelectedCpCodes(values as string[])}
                        filterOption={filterOptionByLabel}
                        maxTagCount="responsive"
                    />
                    {selectedCpCodes.length > CP_WARN_THRESHOLD && (
                        <Typography.Text type="warning" style={{ display: 'block', marginTop: 4 }}>
                            Đã chọn {selectedCpCodes.length} đối tác. Khuyến nghị ≤ {CP_WARN_THRESHOLD} để tránh timeout.
                        </Typography.Text>
                    )}
                    <p>Loại báo cáo</p>
                    <Select
                        style={{ width: '100%' }}
                        value={reportType}
                        onChange={(value) => {
                            setReportType(value)
                            setReportDate('')
                            setReportFromDate(dayjs().format('YYYY-MM-DD'))
                            setReportToDate(dayjs().format('YYYY-MM-DD'))
                        }}
                        options={[
                            { label: 'Theo ngày', value: 'DATE' },
                            { label: 'Theo tháng', value: 'MONTH' },
                            { label: 'Theo quý', value: 'QUARTER' },
                        ]}
                    />
                    {reportType === 'DATE' ? (
                        <>
                            <p>Chọn khoảng thời gian</p>
                            <DatePicker.RangePicker
                                style={{ width: '100%' }}
                                format="YYYY-MM-DD"
                                value={[dayjs(reportFromDate), dayjs(reportToDate)]}
                                allowClear={false}
                                onChange={(_, dateStrings) => {
                                    setReportFromDate(dateStrings[0] as string)
                                    setReportToDate(dateStrings[1] as string)
                                }}
                            />
                        </>
                    ) : (
                        <>
                            <p>Chọn thời gian</p>
                            <DatePicker
                                style={{ width: '100%' }}
                                picker={getDatePicker() as any}
                                format={getDateFormat()}
                                onChange={(_, dateString) => setReportDate(dateString as string)}
                            />
                        </>
                    )}
                </>
            )}
            {reportStatus === 'polling' && (
                <div style={{ textAlign: 'center', padding: 20 }}>
                    <LoadingOutlined style={{ fontSize: 32 }} spin />
                    <p style={{ marginTop: 12 }}>Đang tạo báo cáo... Vui lòng chờ.</p>
                    {progress && progress.total > 0 && (
                        <>
                            <Progress percent={progressPercent} status="active" style={{ marginTop: 8 }} />
                            <p style={{ marginTop: 8, color: '#666' }}>
                                {progress.completed}/{progress.total} đối tác đã xử lý
                                {progress.failed > 0 ? ` (${progress.failed} lỗi)` : ''}
                            </p>
                        </>
                    )}
                </div>
            )}
            {reportStatus === 'success' && (
                <div style={{ textAlign: 'center', padding: 20 }}>
                    <CheckCircleOutlined style={{ fontSize: 32, color: '#52c41a' }} />
                    <p style={{ marginTop: 12 }}>Tải báo cáo thành công!</p>
                    {partialFailures.length > 0 && (
                        <Typography.Text type="warning" style={{ display: 'block', marginTop: 8 }}>
                            {partialFailures.length} đối tác lỗi — xem sheet Summary trong file để biết chi tiết.
                        </Typography.Text>
                    )}
                </div>
            )}
            {reportStatus === 'failed' && (
                <div style={{ textAlign: 'center', padding: 20 }}>
                    <CloseCircleOutlined style={{ fontSize: 32, color: '#ff4d4f' }} />
                    <p style={{ marginTop: 12 }}>{reportError}</p>
                    <Button onClick={() => { setReportStatus('idle'); setReportError(''); setProgress(null); setPartialFailures([]) }}>
                        Thử lại
                    </Button>
                </div>
            )}
            <Divider />
        </Modal>
    )
}

export default ReportDownloadAllModal
